import { useEffect, useState } from "react";
import { imageObjectUrl } from "../api/client";

/**
 * Makes an image usable in an <img> tag whether it is public or behind the
 * API's auth. An <img> cannot send an Authorization header, so a cover served
 * by the API has to be fetched and handed over as an object URL; external
 * thumbnails are returned unchanged.
 *
 * Returns undefined while an API-served image is loading, so the caller can
 * tell "not ready yet" from "no image".
 */
export function useAuthedImage(src: string | null): string | null | undefined {
  const [resolved, setResolved] = useState<string | null | undefined>(
    src && !src.startsWith("/api/") ? src : undefined
  );

  useEffect(() => {
    if (!src) {
      setResolved(null);
      return;
    }
    if (!src.startsWith("/api/")) {
      setResolved(src);
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;
    setResolved(undefined);

    imageObjectUrl(src)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setResolved(url);
      })
      .catch(() => {
        if (!cancelled) setResolved(null);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  return resolved;
}
